import { GateMovementState2 } from '@/__generated__/graphql';
import { GateFigure } from '@components/GateFigure';
import { MovementDataRenderer } from '@features/gateCommon';
import { useStopGateOperation } from '@features/gateCommon/hooks/useStopGateOperation';
import {
  getStateColor,
  isFinalizedState,
  overlayStateMap,
  translateMovementState,
} from '@features/gateCommon/utils/gateMovementState';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Divider, Typography } from '@mui/material';
import { ReactNode, useCallback, type FC } from 'react';
import { ProgressBar } from './ProgressBar';
import { OperationPositionData } from './dataComponents/OperationPositionData';

interface Props {
  movementId: number | null;
}

export const PanelOperation: FC<Props> = ({ movementId }) => {
  const { i18n } = useLingui();
  const { stopOperation, loading } = useStopGateOperation();

  const onStopOperation = useCallback(
    (gateId: string) => {
      stopOperation(gateId);
    },
    [stopOperation],
  );

  return (
    <Box sx={{ px: 4, py: 2, gridArea: 'operation' }}>
      {/* FIGURE AND BUTTONS */}
      <Box
        mb={2}
        display="flex"
        gap={2}
        sx={{ alignItems: 'center', justifyContent: 'space-around' }}
      >
        {movementId && (
          <MovementDataRenderer
            movementId={movementId}
            render={({ movement }) => {
              const { requestedPosition, startPosition } = movement;
              const isUp = startPosition ? requestedPosition > startPosition : undefined;
              const direction = isUp === undefined ? undefined : isUp ? 'up' : 'down';

              const overlayState = overlayStateMap(movement.state);

              return (
                <GateFigure
                  state={overlayState}
                  boxProps={{ width: 185 }}
                  position={movement.lastPosition}
                  moveDirection={direction}
                />
              );
            }}
            fallback={() => {
              return null;
            }}
          />
        )}

        <Box flexGrow={1} maxWidth={500}>
          <OperationPositionData
            movementId={movementId}
            render={({ label, position, relativeTime, color, contrastColor }) => {
              return (
                <Box
                  display="grid"
                  sx={{
                    gridTemplateColumns: '1fr auto 1fr',
                    py: 1,
                    px: 1,
                    placeItems: 'center',
                    border: 1,
                    borderRadius: 1,
                    gap: 2,
                  }}
                >
                  <Typography fontWeight="bold">{label}</Typography>
                  <Divider orientation="vertical" sx={{ borderColor: 'white', mx: 2 }} />
                  <Box
                    sx={{
                      py: 1 / 2,
                      px: 2,
                      bgcolor: color,
                      color: contrastColor,
                      borderRadius: 1,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 15,
                        lineHeight: 1,
                        pb: '1px',
                        color: 'inherit',
                      }}
                    >
                      {position}
                    </Typography>
                    <Typography sx={{ fontSize: 10, lineHeight: 1, color: 'inherit' }}>
                      {relativeTime}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
          />

          <Box display="grid" gap={1.5} mt={2}>
            <Typography fontWeight="bold" px={1}>
              <Trans>Estado de la operación</Trans>
            </Typography>

            {movementId ? (
              <MovementDataRenderer
                movementId={movementId}
                render={({ movement }) => {
                  const label = translateMovementState(movement.state, i18n);
                  const { color } = getStateColor(movement.state);
                  return (
                    <StateDisplay
                      label={label}
                      bgcolor={
                        movement.state === GateMovementState2.Finalized ||
                          movement.state === GateMovementState2.FinalizedWithError
                          ? color
                          : '#34404D'
                      }
                    />
                  );
                }}
                fallback={() => (
                  <StateDisplay label={<Trans>Obteniendo información</Trans>} bgcolor="#34404D" />
                )}
              />
            ) : (
              <StateDisplay label={<Trans>Iniciando operación</Trans>} bgcolor="#34404D" />
            )}

            {movementId ? (
              <MovementDataRenderer
                movementId={movementId}
                render={({ movement: { state }, gate }) => {
                  const finalized = isFinalizedState(state);
                  const gateId = gate.id;

                  return (
                    <LoadingButton
                      loading={loading}
                      disabled={finalized}
                      color="error"
                      size="large"
                      variant="contained"
                      onClick={() => onStopOperation(gateId)}
                      sx={{ fontSize: 16 }}
                    >
                      <Trans>Detener operación</Trans>
                    </LoadingButton>
                  );
                }}
                fallback={() => (
                  <Button
                    disabled
                    color="error"
                    size="large"
                    variant="contained"
                    sx={{ fontSize: 16 }}
                  >
                    <Trans>Detener operación</Trans>
                  </Button>
                )}
              />
            ) : (
              <Button disabled color="error" size="large" variant="contained" sx={{ fontSize: 16 }}>
                <Trans>Detener operación</Trans>
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {movementId ? (
        <MovementDataRenderer
          movementId={movementId}
          render={({ movement }) => {
            const progress = movement.operationProgress;
            const initializing = movement.state === GateMovementState2.InQueue;
            const finalized = isFinalizedState(movement.state);
            const { color } = getStateColor(movement.state);

            return (
              <>
                <ProgressBar
                  label={t(i18n)`Progreso de operación`}
                  value={initializing ? 0 : progress}
                  speed={finalized ? 0 : 3}
                  indeterminate={initializing}
                  color={color}
                />
              </>
            );
          }}
          fallback={() => {
            return (
              <ProgressBar
                label={t(i18n)`Progreso de operación`}
                value={0}
                indeterminate
                color="#ffffff"
              />
            );
          }}
        />
      ) : (
        <ProgressBar
          label={t(i18n)`Progreso de operación`}
          value={0}
          indeterminate
          color="#ffffff"
        />
      )}
    </Box>
  );
};

const StateDisplay: FC<{ bgcolor: string; label: ReactNode }> = ({ bgcolor, label }) => {
  return (
    <Box sx={{ bgcolor: bgcolor, py: 1, px: 2, textAlign: 'center', borderRadius: 1 }}>
      <Typography fontWeight="bold">{label}</Typography>
    </Box>
  );
};
